import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { scrollTo } from "@/lib/scroll";
import { EASE } from "@/lib/motion";

const HERO_IMG =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8a/2024-05-25_Aisle_16%2C_Asda%2C_Inverness.jpg/1280px-2024-05-25_Aisle_16%2C_Asda%2C_Inverness.jpg";

const LINES = [
  { text: "Architecting", cls: "" },
  { text: "high-yield retail", cls: "italic text-forest" },
  { text: "& HoReCa supply chains.", cls: "" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-[72px]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 right-[-15%] h-[640px] w-[640px] rounded-full bg-gold/15 blur-[120px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[480px] w-[480px] rounded-full bg-forest/10 blur-[110px]"
      />

      <motion.div
        style={{ opacity: fade }}
        className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-28 lg:pt-24"
      >
        <div className="lg:col-span-12 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="mb-8 flex items-center gap-4"
          >
            <span className="h-px w-12 bg-gold" />
            <span
              data-testid="hero-overline"
              className="font-mono text-xs font-semibold uppercase tracking-[0.28em] text-forest"
            >
              Retail &amp; HoReCa Advisory — Est. 2017
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-serif text-4xl font-medium leading-[1.08] tracking-tight text-zinc-900 sm:text-5xl lg:text-6xl"
          >
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: EASE }}
                  className={`block ${line.cls}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg"
          >
            We advise supermarket networks and hospitality groups on sourcing,
            private label and trade negotiations — across food and non-food —
            turning procurement into a margin engine, not a cost centre.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollTo("#services")}
              className="group flex items-center gap-3 rounded-full bg-forest px-7 py-4 text-sm font-semibold text-alabaster shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-forest-dark hover:shadow-xl"
            >
              Explore Strategic Services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-12 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400"
          >
            <ArrowDown className="h-3.5 w-3.5 animate-bounce text-gold" />
            Trusted across 18 European markets
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
